/**
 * Google Ads Consent Mode v2 + gtag.js bootstrap.
 *
 * Design: _bmad-output/CONVERSION_TRACKING_ARCHITECTURE_2026-07-08.md
 *   - AD-5: consent default = denied BEFORE the tag; promoted to granted only
 *           on explicit opt-in ('marketing' category).
 *   - AD-6: gtag config uses cookie_domain:'auto' → the _gcl_* cookie is
 *           written on the registrable domain (.sighthost.online) and is
 *           shared across www. + portal., surviving the PayFast round-trip.
 *
 * Config-driven: reads siteConfig.googleAds.conversionId. When it is empty
 * (owner has not yet pasted the AW-… id) the module stays FULLY INERT — the
 * consent defaults are still set (harmless), but no gtag.js is loaded and no
 * network request is made. This lets us ship the whole feature ahead of the
 * owner's Google Ads setup.
 *
 * Placement note vs AD-5 (which said "inline in index.html head"): we call
 * initGoogleAdsConsentMode() at the very top of each app's main.tsx instead.
 * The ordering guarantee (consent default pushed to dataLayer BEFORE gtag.js
 * drains it) is preserved, and it keeps the AW id out of static HTML — one
 * source of truth in siteConfig, no config drift. No other Google tag loads
 * from either index.html, so nothing races this.
 */

import { readConsent, isCategoryAllowed, siteConfig } from '@afrilink/config';

type GtagFn = (...args: unknown[]) => void;

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: GtagFn;
  }
}

function ensureGtag(): GtagFn {
  window.dataLayer = window.dataLayer || [];
  if (!window.gtag) {
    window.gtag = function gtag() {
      // gtag pushes the raw `arguments` object — not an array — by design.
      // eslint-disable-next-line prefer-rest-params
      window.dataLayer!.push(arguments);
    };
  }
  return window.gtag;
}

let initialised = false;

/**
 * Set Consent Mode v2 defaults and, when configured, load gtag.js. Safe to
 * call on every app boot: no-ops after the first call, and stays inert while
 * siteConfig.googleAds.conversionId is empty.
 */
export function initGoogleAdsConsentMode(): void {
  if (typeof window === 'undefined' || initialised) return;
  initialised = true;

  const gtag = ensureGtag();
  gtag('js', new Date());

  // Deny by default (POPIA). wait_for_update gives a stored 'accept' a brief
  // window to be read before any tag fires.
  gtag('consent', 'default', {
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
    analytics_storage: 'denied',
    wait_for_update: 500,
  });

  // Returning visitor who already accepted (shared .sighthost.online cookie)?
  // Promote immediately so this page's conversion can fire.
  if (isCategoryAllowed(readConsent(), 'marketing')) {
    grantAdConsent();
  }

  const { conversionId } = siteConfig.googleAds;
  if (!conversionId) return; // inert until the owner provides the AW-… id

  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${conversionId}`;
  document.head.appendChild(script);
  gtag('config', conversionId, { cookie_domain: 'auto' });
}

/** Flip advertising consent to granted. Idempotent. */
export function grantAdConsent(): void {
  if (typeof window === 'undefined') return;
  ensureGtag()('consent', 'update', {
    ad_storage: 'granted',
    ad_user_data: 'granted',
    ad_personalization: 'granted',
    analytics_storage: 'granted',
  });
}
